import {observer} from 'mobx-react';
import React from 'react';
import {userStatusService, X5LessonSnapshot, X6Page} from '../../../domain';
import {Link, PageHeading, PromiseMessage, PromiseRetry} from '../../../lib/components';
import {dateFormat, ReactNodeGenerator} from '../../../lib/helpers';
import {routes} from '../../../pages/Routes';
import {AddPageControls} from './parts/AddPageControls';
import {AddPageForm} from './parts/AddPageForm';
import {DeletePageControls} from './parts/DeletePageControls';
import {EditPageControls} from './parts/EditPageControls';
import {EditPageForm} from './parts/EditPageForm';
import {MovePageControls} from './parts/MovePageControls';
import {MovePageForm} from './parts/MovePageForm';
import {ReorderPagesControls} from './parts/ReorderPagesControls';
import {ActivateSnapshotControls} from './snapshot-parts/ActivateSnapshotControls';
import {DeactivateSnapshotControls} from './snapshot-parts/DeactivateSnapshotControls';
import {DeleteSnapshotControls} from './snapshot-parts/DeleteSnapshotControls';
import {TakeSnapshotControls} from './snapshot-parts/TakeSnapshotControls';
import {UpdateSnapshotControls} from './snapshot-parts/UpdateSnapshotControls';
import {X5LessonBreadcrumbs} from './X5LessonBreadcrumbs';
import {X5LessonStore} from './X5LessonStore';

function PageViewNoActions(page: X6Page): React.ReactNode {
  return (
    <div className="row" key={'page-' + page.Id}>
      <div className="col">
        <div className="text-box">
          {page.Name}
        </div>
      </div>
    </div>
  );
}

function* ViewNoActions(store: X5LessonStore): ReactNodeGenerator {
  const pages = store.loader.pages;

  if (pages.length === 0)
    yield (
      <div className="row justify-content-center" key="no-pages">
        <div className="col-auto">
          <div className="alert alert-info">
            No pages
          </div>
        </div>
      </div>);

  for (const page of pages)
    yield PageViewNoActions(page);
}

function* ViewModeItems(store: X5LessonStore): ReactNodeGenerator {
  const pages = store.loader.pages;

  if (pages.length === 0)
    yield (
      <div className="row justify-content-center" key="no-pages">
        <div className="col-auto">
          <div className="alert alert-info">
            No pages
          </div>
        </div>
      </div>);

  for (const page of pages) {
    yield (
      <div className="row" key={'page-' + page.Id}>
        <div className="col">
          <div className="text-box">
            {page.Name}
          </div>
        </div>
        <div className="col-auto no-wrap">
          <Link className="btn btn-primary" to={routes.x6page(page.Id)}> View </Link>
          {
            userStatusService.status.roles.ContentManager &&
            <>
            <button type="button" className="btn btn-primary"
                    onClick={() => store.beginEditPage(page.Id)}> Edit
            </button>
            <button type="button" className="btn btn-primary"
                    onClick={() => store.beginMovePage(page.Id)}> Move
            </button>
            <button type="button" className="btn btn-danger"
                    onClick={() => store.beginDeletePage(page.Id)}> Delete
            </button>
            </>
          }
        </div>
      </div>);
  }
}

function* AddModeItems(store: X5LessonStore): ReactNodeGenerator {
  const pages = store.loader.pages;

  yield (<AddPageForm key="add-page-form" store={store}/>);

  yield (<AddPageControls key="add-page-controls" store={store}/>);

  for (const page of pages)
    yield PageViewNoActions(page);
}

function* EditModeItems(store: X5LessonStore): ReactNodeGenerator {
  const pages = store.loader.pages;
  const editPageId = store.editPage!.page.Id;

  for (const page of pages) {
    if (page.Id !== editPageId) {
      yield PageViewNoActions(page);
      continue;
    }

    yield (<EditPageForm key="edit-page-form" store={store}/>);

    yield (<EditPageControls key="edit-page-controls" store={store}/>);
  }
}

function* MoveModeItems(store: X5LessonStore): ReactNodeGenerator {
  const pages = store.loader.pages;
  const movePageId = store.move!.pageId;

  for (const page of pages) {
    yield PageViewNoActions(page);

    if (page.Id !== movePageId)
      continue;

    yield (<MovePageForm key="move-lesson-form" store={store}/>);

    yield (<MovePageControls key="move-lesson-controls" store={store}/>);
  }
}

function* DeleteModeItems(store: X5LessonStore): ReactNodeGenerator {
  const pages = store.loader.pages;
  const deletePageId = store.deletePage!.pageId;

  for (let page of pages) {
    yield PageViewNoActions(page);

    if (page.Id !== deletePageId)
      continue;

    yield (<DeletePageControls key="delete-page-controls" store={store}/>);
  }
}

function* ReorderModeItems(store: X5LessonStore): ReactNodeGenerator {
  const pageIds = store.reorder!.pageIds;
  const pageById = store.loader.pageById;

  for (let index = 0; index < pageIds.length; index++) {
    let pageId = pageIds[index];
    const page = pageById[pageId];
    const first = index === 0;
    const last = index === pageIds.length - 1;

    yield (
      <div className="row" key={'page-' + page.Id}>
        <div className="col">
          <div className="text-box">
            {page.Name}
          </div>
        </div>
        <div className="col-auto no-wrap">
          <button type="button" className="btn btn-sm"
                  onClick={() => store.reorderUp(index)}
                  disabled={first}>
            <span className="fas fa-arrow-up"/>
          </button>
          <button type="button" className="btn btn-sm"
                  onClick={() => store.reorderDown(index)}
                  disabled={last}>
            <span className="fas fa-arrow-down"/>
          </button>
        </div>
      </div>);
  }

  yield (<ReorderPagesControls key="reorder-pages-controls" store={store}/>);
}

function* cardBodyRenderer(store: X5LessonStore): ReactNodeGenerator {
  const mode = store.mode;

  switch (mode) {
    case 'view':
      yield* ViewModeItems(store);
      break;
    case 'add-page':
      yield* AddModeItems(store);
      break;
    case 'edit-page':
      yield* EditModeItems(store);
      break;
    case 'delete-page':
      yield* DeleteModeItems(store);
      break;
    case 'reorder-pages':
      yield* ReorderModeItems(store);
      break;
    case 'move-page':
      yield* MoveModeItems(store);
      break;
    case 'take-snapshot':
    case 'update-snapshot':
    case 'activate-snapshot':
    case 'deactivate-snapshot':
    case 'delete-snapshot':
      yield* ViewNoActions(store);
      break;
  }
}

function ViewSnapshotNoActions(snapshot: X5LessonSnapshot): React.ReactNode {
  return (
    <div className="row" key={'snapshot-' + snapshot.Id}>
      <div className="col">
        <div className="text-box">
          {snapshot.Version}
        </div>
      </div>
      <div className="col">
        <div className="text-box">
          {dateFormat(snapshot.CreatedOn, 'wymdhms', true)}
        </div>
      </div>
    </div>
  );
}

function* ViewModeSnapshotItems(store: X5LessonStore): ReactNodeGenerator {
  const snapshots = store.loader.lesson?.Snapshots;
  if (snapshots === null || snapshots === undefined) {
    yield (
      <div className="row justify-content-center" key="no-snapshots">
        <div className="col-auto">
          <div className="alert alert-info">No snapshots</div>
        </div>
      </div>);
    return;
  }
  for (let snapshot of snapshots) {
    yield <div className="row" key={snapshot.Id}>
      <div className="col-4">
        <div className="text-box">
          {snapshot.Version}
        </div>
      </div>
      <div className="col-4">
        <div className="text-box">
          {dateFormat(snapshot.CreatedOn, 'wymdhms', true)}
        </div>
      </div>
      <div className="col-auto ml-auto">
        {snapshot.Active === true &&
        <button className="btn btn-primary"
                onClick={() => store.beginDeactivateSnapshot(snapshot)}>
          Deactivate
        </button>}
        {snapshot.Active === false &&
        <button className="btn btn-primary"
                onClick={() => store.beginActivateSnapshot(snapshot)}>
          Activate
        </button>}

        <button className="btn btn-primary"
                onClick={() => store.beginUpdateSnapshot(snapshot)}>
          Update
        </button>
        <button className="btn btn-primary"
                onClick={() => store.beginDeleteSnapshot(snapshot.Id)}
                disabled={snapshot.Active === true}>
          Delete
        </button>
      </div>
    </div>;
  }
}

function* TakeSnapshotModeItems(store: X5LessonStore): ReactNodeGenerator {
  yield (<TakeSnapshotControls key="take-snapshot-controls" store={store}/>);

  const snapshots = store.loader.lesson?.Snapshots;
  if (snapshots === null || snapshots === undefined)
    return;

  for (let snapshot of snapshots)
    yield ViewSnapshotNoActions(snapshot);
}

function* UpdateSnapshotItems(store: X5LessonStore): ReactNodeGenerator {
  const updateSnapshotId = store.updateSnapshot!.snapshot.Id;

  const snapshots = store.loader.lesson?.Snapshots;
  if (snapshots === null || snapshots === undefined)
    return;

  for (let snapshot of snapshots)
    if (snapshot.Id === updateSnapshotId)
      yield(<UpdateSnapshotControls key="update-snapshot-controls" store={store}/>);
    else
      yield ViewSnapshotNoActions(snapshot);
}

function* ActivateSnapshotItems(store: X5LessonStore): ReactNodeGenerator {
  const activateSnapshotId = store.activateSnapshot!.snapshot.Id;

  const snapshots = store.loader.lesson?.Snapshots;
  if (snapshots === null || snapshots === undefined)
    return;

  for (let snapshot of snapshots) {
    yield ViewSnapshotNoActions(snapshot);

    if (snapshot.Id === activateSnapshotId)
      yield(<ActivateSnapshotControls key="activate-snapshot-controls" store={store}/>);
  }
}

function* DeactivateSnapshotItems(store: X5LessonStore): ReactNodeGenerator {
  const deactivateSnapshotId = store.deactivateSnapshot!.snapshot.Id;

  const snapshots = store.loader.lesson?.Snapshots;
  if (snapshots === null || snapshots === undefined)
    return;

  for (let snapshot of snapshots) {
    yield ViewSnapshotNoActions(snapshot);

    if (snapshot.Id === deactivateSnapshotId)
      yield(<DeactivateSnapshotControls key="deactivate-snapshot-controls" store={store}/>);
  }
}

function* DeleteSnapshotModeItems(store: X5LessonStore): ReactNodeGenerator {
  const snapshots = store.loader.lesson?.Snapshots!;
  const deleteSnapshotId = store.deleteSnapshot!.lessonSnapshotId;

  for (let snapshot of snapshots) {
    yield ViewSnapshotNoActions(snapshot);

    if (snapshot.Id === deleteSnapshotId)
      yield (<DeleteSnapshotControls key="delete-snapshot-controls" store={store}/>);
  }
}

function* snapshotsBodyRenderer(store: X5LessonStore): ReactNodeGenerator {
  const mode = store.mode;

  switch (mode) {
    case 'view':
      yield* ViewModeSnapshotItems(store);
      break;
    case 'take-snapshot':
      yield* TakeSnapshotModeItems(store);
      break;
    case 'update-snapshot':
      yield* UpdateSnapshotItems(store);
      break;
    case 'activate-snapshot':
      yield* ActivateSnapshotItems(store);
      break;
    case 'deactivate-snapshot':
      yield* DeactivateSnapshotItems(store);
      break;
    case 'delete-snapshot':
      yield* DeleteSnapshotModeItems(store);
      break;
    case 'add-page':
    case 'edit-page':
    case 'delete-page':
    case 'reorder-pages':
    case 'move-page':
    default:
      return;
  }
}

function* sceneRenderer(store: X5LessonStore): ReactNodeGenerator {
  const lookupState = store.loader.lookupState!;
  const lessonState = store.loader.lessonState!;

  if (lookupState === null || lessonState === null)
    return;

  if (lookupState.isPending || lookupState.isRejected ||
    lessonState.isPending || lessonState.isRejected) {
    yield (<PageHeading key="heading"> Lesson </PageHeading>);

    yield(<PromiseMessage key="lookup-loader"
                          label="loading lesson" state={lookupState} align="right"/>);

    yield (<PromiseRetry key="lookup-retry"
                         state={lookupState} retry={store.loader.loadLookup} align="right"/>);

    yield(<PromiseMessage key="lesson-loader"
                          label="loading lesson" state={lessonState} align="right"/>);

    yield (<PromiseRetry key="lesson-retry"
                         state={lessonState} retry={store.loader.loadLesson} align="right"/>);

    return;
  }

  const lesson = store.loader.lesson!;
  if (!lookupState.isResolved || !lessonState.isResolved || lesson === null)
    return;

  const mode = store.mode;
  yield (<X5LessonBreadcrumbs key="lesson-breadcrumbs"
                              lesson={lesson}/>);

  yield (<PageHeading key="heading"> Lesson {lesson!.Name} </PageHeading>);

  yield (
    <div className="row" key="main">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <div className="row align-items-center">
              <div className="col"> Pages</div>
              {
                mode === 'view' &&
                <div className="col-auto no-wrap">
                  {
                    userStatusService.status.roles.ContentManager &&
                    <>
                     <button type="button" className="btn btn-primary"
                              onClick={store.beginAddPage}> Add
                      </button>
                      <button type="button" className="btn btn-primary"
                              disabled={store.loader.pages.length < 2}
                              onClick={store.beginReorderPages}> Reorder
                      </button>
                    </>
                  }
                </div>
              }
            </div>
          </div>

          <div className="card-body">
            {Array.from(cardBodyRenderer(store))}
          </div>
        </div>
      </div>
    </div>
  );

  if (userStatusService.status.roles.ContentManager)
    if (mode === 'view' || mode === 'take-snapshot' || mode === 'update-snapshot' ||
      mode === 'activate-snapshot' || mode === 'deactivate-snapshot' || mode === 'delete-snapshot')
      yield (
        <div className="row" key="lesson-snapshots">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <div className="row">
                  <div className="col">Snapshots</div>
                  {store.mode === 'view' &&
                  <div className="col-auto">
                    <button type="button" className="btn btn-primary"
                            onClick={store.beginTakeSnapshot}> Take snapshot
                    </button>
                  </div>
                  }
                </div>
              </div>
              <div className="card-body">
                {Array.from(snapshotsBodyRenderer(store))}
              </div>
            </div>
          </div>
        </div>
      );
}

interface Props {
  id: number;
}

@observer
export class X5LessonComponent extends React.Component<Props> {
  readonly store: X5LessonStore;

  constructor(props: Readonly<Props>) {
    super(props);
    this.store = new X5LessonStore(props.id);
  }

  componentDidMount() {
    this.store.loader.loadLookup();
    this.store.loader.loadLesson();
  }

  render() {
    return Array.from(sceneRenderer(this.store));
  }

  componentWillUnmount(): void {
    this.store.dispose();
  }
}
